import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api from '../lib/api';
import { 
    Upload, 
    Link2, 
    Image as ImageIcon, 
    Loader2, 
    X,
    Camera,
    Check
} from 'lucide-react';
import { toast } from 'sonner';

export const ImageSelector = ({ value, onChange, label = "Image" }) => {
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState(value?.startsWith('http') || value?.startsWith('/uploads') ? 'url' : 'upload');
    const [previewUrl, setPreviewUrl] = useState(value || '');
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Get file extension
        const fileName = file.name.toLowerCase();
        const fileExt = fileName.split('.').pop();
        
        // Valid extensions (including iPhone formats)
        const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
        
        // Check by extension first (more reliable for iPhone photos)
        const isValidExtension = validExtensions.includes(fileExt);
        
        // Also check MIME type as fallback
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
        const isValidType = validTypes.includes(file.type) || file.type.startsWith('image/');
        
        if (!isValidExtension && !isValidType) {
            toast.error('Type de fichier non supporté. Utilisez JPG, PNG, GIF, WEBP ou HEIC.');
            return;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 10MB)');
            return;
        }

        setUploading(true);
        try {
            const response = await api.uploadImage(file);
            const imageUrl = response.data.url;
            
            setPreviewUrl(imageUrl);
            onChange(imageUrl);
            toast.success('Photo téléversée avec succès!');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
        } finally {
            setUploading(false);
        }
    };

    const handleUrlChange = (url) => {
        setPreviewUrl(url);
        onChange(url);
    };

    const clearImage = () => {
        setPreviewUrl('');
        onChange('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-3">
            <Label>{label}</Label>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Téléverser
                    </TabsTrigger>
                    <TabsTrigger value="url" className="flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        URL Internet
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="upload" className="mt-3">
                    <div 
                        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                            uploading ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.heic,.heif"
                            capture="environment"
                            onChange={handleFileSelect}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={uploading}
                        />
                        
                        {uploading ? (
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">Téléversement en cours...</p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                                    <Upload className="w-6 h-6 text-primary" />
                                </div>
                                <p className="text-sm font-medium">Cliquez ou glissez une photo</p>
                                <p className="text-xs text-muted-foreground">
                                    JPG, PNG, GIF, WEBP • Max 10MB
                                </p>
                            </div>
                        )}
                    </div>
                </TabsContent>
                
                <TabsContent value="url" className="mt-3">
                    <Input
                        type="url"
                        placeholder="https://example.com/image.jpg"
                        value={previewUrl}
                        onChange={(e) => handleUrlChange(e.target.value)}
                        className="rounded-xl"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        Collez l&apos;URL d&apos;une image trouvée sur Internet
                    </p>
                </TabsContent>
            </Tabs>

            {/* Preview */}
            {previewUrl && (
                <div className="relative mt-3">
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-muted">
                        <img 
                            src={previewUrl} 
                            alt="Aperçu" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="hidden absolute inset-0 items-center justify-center bg-muted">
                            <div className="text-center">
                                <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground/50" />
                                <p className="text-sm text-muted-foreground">Image non disponible</p>
                            </div>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 rounded-full"
                        onClick={clearImage}
                    >
                        <X className="w-4 h-4" />
                    </Button>
                    <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Image sélectionnée
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageSelector;
